import { z } from 'zod';
import { dateTimeSchema, uidSchema } from './shared';
import { CarrierCode } from '@util/maps/carriers';
import { ChipColor } from '@ui/Chip';
import { addressSchema } from './address';

export enum ReturnState {
  ReturnRequested,
  AwaitingDropoff,
  ReturnDenied,
  InTransit,
  ReturnDelivered,
  ReturnComplete,
  OnHold,
  ReturnCancelled,
}

export const ReturnStateLabels = {
  [ReturnState.ReturnRequested]: 'Return Requested',
  [ReturnState.AwaitingDropoff]: 'Awaiting Dropoff',
  [ReturnState.ReturnDenied]: 'Return Denied',
  [ReturnState.InTransit]: 'In Transit',
  [ReturnState.ReturnDelivered]: 'Return Delivered',
  [ReturnState.ReturnComplete]: 'Return Complete',
  [ReturnState.OnHold]: 'On Hold',
  [ReturnState.ReturnCancelled]: 'Return Cancelled',
} as const;

export const ReturnStateColors: Record<ReturnState, ChipColor> = {
  [ReturnState.ReturnRequested]: 'active',
  [ReturnState.AwaitingDropoff]: 'blue',
  [ReturnState.ReturnDenied]: 'error',
  [ReturnState.InTransit]: 'orange',
  [ReturnState.ReturnDelivered]: 'success',
  [ReturnState.ReturnComplete]: 'purple',
  [ReturnState.OnHold]: 'warning',
  [ReturnState.ReturnCancelled]: 'error',
} as const;

export const ReturnStateFilterOptions: {
  id: ReturnState;
  value: string;
  label: (typeof ReturnStateLabels)[keyof typeof ReturnStateLabels];
}[] = [
  {
    id: ReturnState.ReturnRequested,
    value: ReturnState.ReturnRequested.toString(),
    label: ReturnStateLabels[ReturnState.ReturnRequested],
  },
  {
    id: ReturnState.AwaitingDropoff,
    value: ReturnState.AwaitingDropoff.toString(),
    label: ReturnStateLabels[ReturnState.AwaitingDropoff],
  },
  {
    id: ReturnState.ReturnDenied,
    value: ReturnState.ReturnDenied.toString(),
    label: ReturnStateLabels[ReturnState.ReturnDenied],
  },
  {
    id: ReturnState.InTransit,
    value: ReturnState.InTransit.toString(),
    label: ReturnStateLabels[ReturnState.InTransit],
  },
  {
    id: ReturnState.ReturnDelivered,
    value: ReturnState.ReturnDelivered.toString(),
    label: ReturnStateLabels[ReturnState.ReturnDelivered],
  },
  {
    id: ReturnState.ReturnComplete,
    value: ReturnState.ReturnComplete.toString(),
    label: ReturnStateLabels[ReturnState.ReturnComplete],
  },
  {
    id: ReturnState.OnHold,
    value: ReturnState.OnHold.toString(),
    label: ReturnStateLabels[ReturnState.OnHold],
  },
  {
    id: ReturnState.ReturnCancelled,
    value: ReturnState.ReturnCancelled.toString(),
    label: ReturnStateLabels[ReturnState.ReturnCancelled],
  },
];

export const returnSchema = z.object({
  id: z.string().min(10),
  approval_date: dateTimeSchema.optional(),
  buyer_id: uidSchema,
  carrier: z
    .string()
    .transform((x) => x.toLowerCase() as CarrierCode)
    .nullable()
    .default(null),
  chat_id: z.string().nullable().default(null),
  charge_id: z.string().optional(),
  created: dateTimeSchema,
  eligible_refund_amount: z.number().gte(0),
  label_cost: z.number().gte(0).nullable().default(null),
  label_id: z.string().nullable().default(null),
  label_pdf: z.string().nullable().default(null),
  images: z
    .array(
      z.object({
        thumb: z.string(),
        download_url: z.string().optional(),
        full: z.string(),
      })
    )
    .default([])
    .optional(),
  is_warehouse_return: z.boolean().default(false),
  order_id: z.string(),
  product_id: z.string(),
  ra_fee: z.number().gte(0),
  rate_id: z.string().nullable().default(null),
  restocking_fee: z.number().gte(0).nullable().default(null),
  refund_amount: z.number().gte(0).nullable().default(null),
  refund_reason: z.string().nullable().default(null),
  return_shipping_estimate: z.number().gte(0).default(0),
  service_code: z.string().nullable().default(null),
  seller_id: uidSchema,
  ship_to: addressSchema.optional(), // added when approved
  state: z.nativeEnum(ReturnState).default(ReturnState.ReturnRequested),
  tracking_number: z.string().nullable().default(null),
  final_amount: z.number().gte(0).nullable().default(null),
  // warehouse specific fields
  relisted_product_id: z.string().optional(),
  received_by_warehouse: z.enum(['yes', 'needs_attention']).optional(), // yes means the item was received in good condition, needs_attention means the item was not received in good condition
  warehouse_comments: z.string().optional(),
  // admin specific fields
  notes: z
    .array(
      z.object({
        text: z.string(),
        created: z.number().optional().default(Date.now()),
      })
    )
    .optional(),
});

export interface ReturnInput extends z.input<typeof returnSchema> {}
export interface ReturnDocument extends z.output<typeof returnSchema> {}
